import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";

const Schedule = () => {
  const [canScrollRight, setCanScrollRight] = useState(false);
  const tableContainerRef = useRef(null);
  const scheduleData = [
    {
      type: "Registration",
      time: "8:30 – 9:30 AM",
      event: "Registration & Breakfast",
      speaker: "Check-in",
    },
    {
      type: "Main Event",
      time: "9:30 – 10:00 AM",
      event: "Welcome & Keynote by Andrew Ng",
      speaker: "Keynote presentation",
    },
    {
      type: "Main Event",
      time: "10:00 – 10:30 AM",
      event: (
        <>
          AI-Assisted Coding Panel by Michele Catasta, Chao Peng, and Paxton
          Maeder-York moderated by Eli Chen
        </>
      ),
      speaker: "Panel discussion",
    },
    {
      type: "Build",
      time: "10:30 – 5:00 PM",
      event: "Build Time",
      speaker: "Self-directed",
    },
    {
      type: "Social",
      time: "12:30 – 1:30 PM",
      event: "Networking Lunch",
      speaker: "Open networking",
    },
    {
      type: "Build",
      time: "5:00 – 6:00 PM",
      event: "Semi-Final Build Time",
      speaker: "Self-directed",
    },
    {
      type: "Social",
      time: "5:30 – 6:00 PM",
      event: (
        <>Fireside Chat with Tengyu Ma and Yaad Oren moderated by Eli Chen</>
      ),
      speaker: "Presentation",
    },
    {
      type: "Main Event",
      time: "6:00 – 7:00 PM",
      event: "Demo and Judging",
      speaker: "Finalist presentations",
    },
    {
      type: "Awards",
      time: "7:00 – 7:30 PM",
      event: "Awards and Closing Remarks",
      speaker: "Award ceremony",
    },
    {
      type: "Social",
      time: "7:30 – 8:30 PM",
      event: "Networking Dinner",
      speaker: "Open networking",
    },
  ];

  useEffect(() => {
    const checkScrollable = () => {
      const container = tableContainerRef.current;
      if (container) {
        const { scrollLeft, scrollWidth, clientWidth } = container;
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
      }
    };

    const handleScroll = () => {
      checkScrollable();
    };

    checkScrollable();

    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      window.addEventListener("resize", checkScrollable);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
      window.removeEventListener("resize", checkScrollable);
    };
  }, []);

  const scrollToRight = () => {
    const container = tableContainerRef.current;
    if (container) {
      container.scrollTo({
        left: container.scrollWidth,
        behavior: "smooth",
      });
    }
  };

  const getTypeBadgeClasses = (type) => {
    const baseClasses =
      "inline-block px-3 py-1.5 rounded-full text-sm font-medium text-center min-w-[80px]";

    switch (type) {
      case "Registration":
        return `${baseClasses} bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-700`;
      case "Main Event":
        return `${baseClasses} bg-aifund/10 text-aifund dark:bg-aifund/20 dark:text-aifund-light border border-aifund/20 dark:border-aifund/30`;
      case "Social":
        return `${baseClasses} bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-700`;
      case "Build":
        return `${baseClasses} bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border border-purple-200 dark:border-purple-700`;
      case "Awards":
        return `${baseClasses} bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border border-orange-200 dark:border-orange-700`;
      case "Setup":
        return `${baseClasses} bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-700`;
      default:
        return `${baseClasses} bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-600`;
    }
  };

  return (
    <div className="py-20 bg-white dark:bg-gray-900 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Event Schedule
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-4">
            A complete timeline of our buildathon day
          </p>
        </motion.div>

        {/* Schedule Table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700 relative">
            <div className="overflow-x-auto" ref={tableContainerRef}>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700">
                    <th className="px-6 py-4 text-left font-semibold text-gray-900 dark:text-white text-base border-b-2 border-gray-200 dark:border-gray-600">
                      Type
                    </th>
                    <th className="px-6 py-4 text-left font-semibold text-gray-900 dark:text-white text-base border-b-2 border-gray-200 dark:border-gray-600">
                      Time
                    </th>
                    <th className="px-6 py-4 text-left font-semibold text-gray-900 dark:text-white text-base border-b-2 border-gray-200 dark:border-gray-600">
                      Event
                    </th>
                    <th className="px-6 py-4 text-left font-semibold text-gray-900 dark:text-white text-base border-b-2 border-gray-200 dark:border-gray-600">
                      Format
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {scheduleData.map((event, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                    >
                      <td className="px-6 py-4 align-middle">
                        <span className={getTypeBadgeClasses(event.type)}>
                          {event.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 align-middle font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {event.time}
                      </td>
                      <td className="px-6 py-4 align-middle font-medium text-gray-900 dark:text-white">
                        {event.event}
                      </td>
                      <td className="px-6 py-4 align-middle text-gray-600 dark:text-gray-300">
                        {event.speaker}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Right Chevron Icon */}
            <div
              className={`absolute right-4 top-2 transition-opacity duration-300 ${
                canScrollRight ? "opacity-100" : "opacity-0"
              }`}
            >
              <button
                onClick={scrollToRight}
                className="bg-white dark:bg-gray-700 rounded-full p-2 shadow-md dark:shadow-white/20 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200 cursor-pointer"
              >
                <svg
                  className="w-4 h-4 text-gray-600 dark:text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Schedule;
